import React, { useEffect, useState } from "react";
import { Pagination } from "react-bootstrap";

type MyPaginationProps = {
  total: number;
  onPaginate: (page: number, perPage: number) => void | Promise<void>;
  forceRefresh?: unknown;
};

export default function MyPagination({
  total,
  onPaginate,
  forceRefresh = "",
}: MyPaginationProps) {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(12);
  const firstContact = page * perPage + 1;
  const lastContact = Math.min((page + 1) * perPage, total);
  const totalPages = Math.ceil(total / perPage);

  const focusCurrentPageLink = () => {
    const focusable = document.querySelector(
      ".pagination .page-item.active .page-link"
    ) as HTMLElement | null;
    focusable?.focus();
  };

  useEffect(() => {
    onPaginate(page, perPage);
  }, [page, perPage, forceRefresh]);

  return (
    <div className="my-3 d-flex justify-content-between flex-wrap align-items-center">
      {total > perPage ? (
        <Pagination className="gap-1">
          <Pagination.Prev
            linkClassName="rounded-3"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
          />

          {page > 1 && (
            <Pagination.Item
              linkClassName="rounded-3"
              onClick={() => {
                setPage(0);
                focusCurrentPageLink();
              }}
              disabled={page === 0}
            >
              1
            </Pagination.Item>
          )}

          {page > 2 && <Pagination.Ellipsis linkClassName="rounded-3" />}

          {page > 0 && (
            <Pagination.Item
              linkClassName="rounded-3"
              onClick={() => {
                setPage((p) => p - 1);
                focusCurrentPageLink();
              }}
            >
              {page}
            </Pagination.Item>
          )}

          {/* Current page */}
          <Pagination.Item className="active" linkClassName="rounded-3">
            {page + 1}
          </Pagination.Item>

          {page < totalPages - 1 && (
            <Pagination.Item
              linkClassName="rounded-3"
              onClick={() => {
                setPage((p) => p + 1);
                focusCurrentPageLink();
              }}
            >
              {page + 2}
            </Pagination.Item>
          )}

          {page < totalPages - 3 && (
            <Pagination.Ellipsis linkClassName="rounded-3" />
          )}

          {page < totalPages - 2 && (
            <Pagination.Item
              onClick={() => {
                setPage(totalPages - 1);
                focusCurrentPageLink();
              }}
              linkClassName="rounded-3"
              disabled={page === totalPages - 1}
            >
              {totalPages}
            </Pagination.Item>
          )}

          <Pagination.Next
            linkClassName="rounded-3"
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages - 1}
          />
        </Pagination>
      ) : (
        <div />
      )}
      {total > 0 && (
        <div>
          Showing {firstContact}
          {lastContact > firstContact && <> to {lastContact}</>} of {total}{" "}
          items.
          <select
            className="border-0 px-1 me-2"
            value={perPage}
            onChange={(e) => {
              setPage(0);
              setPerPage(Number(e.target.value));
            }}
          >
            {[12, 36, 96].map((pp) => (
              <option key={pp} value={pp}>
                {pp}
              </option>
            ))}
          </select>
          items per page.
        </div>
      )}
    </div>
  );
}
